export const checkPermission = (routeRole: any, userRole: any) => {
  if (!routeRole || !routeRole) {
    return true;
  }

  if (userRole && Array.isArray(userRole) && !Array.isArray(routeRole)) {
    return userRole.indexOf(routeRole) >= 0;
  }
  if (routeRole.length === 0) {
    return !userRole || userRole.length === 0;
  }
  if (userRole && Array.isArray(userRole) && Array.isArray(routeRole)) {
    return routeRole.some((r) => userRole.indexOf(r) >= 0);
  }
  return routeRole.indexOf(userRole) >= 0;
};

export type PermissionMapEntry = {
  permissionNames?: string[];
  noCheckPermissionWith?: string[];
  allowedRoles?: string[];
};

/**
 * Build a flat map of url → { permissionNames, noCheckPermissionWith } from routeConfig.
 * This allows RouteGenerator to enforce the same permission checks as the menu filter.
 */
export const buildPermissionMap = (
  routeConfig: any[],
): Map<string, PermissionMapEntry> => {
  const map = new Map<string, PermissionMapEntry>();

  const traverse = (items: any[]) => {
    for (const item of items) {
      if (
        item.url &&
        (item.permissionNames ||
          item.noCheckPermissionWith ||
          item.allowedRoles)
      ) {
        const normalizedUrl = item.url.startsWith('/')
          ? item.url
          : `/${item.url}`;
        const entry: PermissionMapEntry = {};
        if (item.permissionNames) entry.permissionNames = item.permissionNames;
        if (item.noCheckPermissionWith)
          entry.noCheckPermissionWith = item.noCheckPermissionWith;
        if (item.allowedRoles) entry.allowedRoles = item.allowedRoles;
        map.set(normalizedUrl, entry);
      }
      if (item.children) traverse(item.children);
    }
  };

  traverse(routeConfig);
  return map;
};

/**
 * Check if the user has the required permissions for a given route path.
 * Matches the route path against the permission map using prefix matching.
 */
export const checkRoutePermission = (
  path: string,
  userRole: any,
  userPermissions: string[],
  permissionMap: Map<string, PermissionMapEntry>,
): boolean => {
  if (!permissionMap || permissionMap.size === 0) return true;

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Find the best matching permission entry (longest prefix match)
  let bestMatch: PermissionMapEntry | null = null;
  let bestLength = 0;

  for (const [url, entry] of permissionMap) {
    if (
      (normalizedPath === url || normalizedPath.startsWith(`${url}/`)) &&
      url.length > bestLength
    ) {
      bestMatch = entry;
      bestLength = url.length;
    }
  }

  // No permission entry found for this path — allow access
  if (!bestMatch) return true;

  const currentRole = Array.isArray(userRole) ? userRole : [userRole];

  // Role gate — mirror the menu's allowedRoles: if the user's role isn't in
  // the allowed list, deny (menu hides the item for that role). No admin
  // bypass here — admins are subject to permission checks just like the menu.
  if (bestMatch.allowedRoles?.length) {
    const roleAllowed = bestMatch.allowedRoles.some((role) =>
      currentRole.includes(role),
    );
    if (!roleAllowed) return false;
  }

  // Skip permission check if user's role is in noCheckPermissionWith
  if (bestMatch.noCheckPermissionWith?.length) {
    const shouldSkip = bestMatch.noCheckPermissionWith.some((role) =>
      currentRole.includes(role),
    );
    if (shouldSkip) return true;
  }

  // Check permissionNames
  if (bestMatch.permissionNames?.length) {
    return bestMatch.permissionNames.some((pn) =>
      userPermissions.includes(pn),
    );
  }

  return true;
};
